import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { NestedTreeControl } from '@angular/cdk/tree';
import { Component, HostListener, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatMenuTrigger } from '@angular/material/menu';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import { NavigationEnd, Router } from '@angular/router';
import { MainTreeMenuData, SideMenuNode } from '@bo-client/core/navigation/main-menu.definition';
import { MenuModel } from '@bo-client/core/navigation/menu.model';
import { NavigationService } from '@bo-client/core/navigation/navigation.service';
import { SideNavFavoritesService } from '@bo-client/core/navigation/side-nav/side-nav-favorites.service';
import { TenantDto } from '@bo-client/shared/models/tenantDto';
import { DataShareService } from '@bo-client/shared/services/data-share.service';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { combineLatestWith, filter, map, startWith, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss']
})
export class SideNavComponent implements OnInit, OnDestroy {
  @Output() menuExpanded = new BehaviorSubject<boolean>(false);
  @ViewChild('favoriteMenuTrigger', { read: MatMenuTrigger }) favoriteMenuTrigger: MatMenuTrigger;

  public treeControl = new NestedTreeControl<SideMenuNode>((node) => node.children);
  public dataSource = new MatTreeNestedDataSource<SideMenuNode>();
  public currentUrl = '';
  private compUnsubs$ = new Subject<void>();

  /** Sichtbare Favoriten in gespeicherter Reihenfolge (Sektion „Favoriten"). */
  public favorites: SideMenuNode[] = [];
  /** Verschiebe-Modus der Favoriten-Sektion: Drag & Drop statt Navigation. */
  public moveMode = false;
  /** Im Verschiebe-Modus bearbeitete Reihenfolge; gespeichert wird erst mit „Fertig". */
  public moveOrder: SideMenuNode[] = [];
  /** Auf Touch-Geräten startet ein Drag erst nach kurzem Halten, damit Scrollen keinen Drag auslöst. */
  public readonly favoriteDragStartDelay = { touch: 200, mouse: 0 };
  /** Mausposition, an der das Favoriten-Menü aufgeht. */
  public favoriteMenuPosition = { x: 0, y: 0 };
  private favoriteLinks = new Set<string>();
  private filteredMenu$ = new BehaviorSubject<SideMenuNode[]>([]);
  /** Die Navigation wurde nur für den Verschiebe-Modus aufgeklappt und klappt danach wieder ein. */
  private collapseAfterMove = false;

  // Neue Properties für das Autocomplete
  tenantFilterControl = new FormControl();
  filteredTenants: Observable<TenantDto[]>;

  constructor(
    private navigationService: NavigationService,
    private favoritesService: SideNavFavoritesService,
    public dataShareService: DataShareService,
    private router: Router
  ) {
    const menuState = localStorage.getItem('menu_expanded');
    this.menuExpanded = new BehaviorSubject(menuState === null || menuState === 'true');

    this.router.events.pipe(takeUntil(this.compUnsubs$)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.currentUrl = decodeURI(event.urlAfterRedirects.substr(1));
      }
    });
  }

  ngOnInit() {
    this.dataShareService.userData$
      .pipe(
        takeUntil(this.compUnsubs$),
        combineLatestWith(this.dataShareService.tenantConfig$, this.dataShareService.uiAdaptions$),
        filter(([userData, tenantConfig, uiAdaptions]) => !!userData && !!tenantConfig && !!uiAdaptions)
      )
      .subscribe(() => {
        this.dataSource.data = this.navigationService.generateTreeMenu(MainTreeMenuData);
        this.filteredMenu$.next(this.dataSource.data);
      });

    // Favoriten laufen über den bereits gefilterten Baum und zeigen nur zulässige Einträge
    combineLatest([this.filteredMenu$, this.favoritesService.favoriteLinks$])
      .pipe(takeUntil(this.compUnsubs$))
      .subscribe(([menu, links]) => {
        this.favoriteLinks = new Set(links);
        this.favorites = this.favoritesService.resolve(menu, links);
      });

    // Initialisierung des aktuellen Tenants im FormControl
    this.dataShareService.currentTenant$.pipe(takeUntil(this.compUnsubs$)).subscribe((tenant) => {
      if (tenant) {
        this.tenantFilterControl.setValue(tenant);
      }
    });

    // Filterlogik für das Autocomplete
    this.filteredTenants = this.tenantFilterControl.valueChanges.pipe(
      startWith(''),
      map((value) => {
        const filterValue = typeof value === 'string' ? value.toLowerCase() : '';
        return this.filterTenants(filterValue);
      })
    );
  }

  // Filterfunktion für Tenants
  private filterTenants(value: string): TenantDto[] {
    const tenants = this.dataShareService.availableTenants;
    if (!value) {
      return tenants;
    }

    return tenants.filter((tenant) => tenant.displayName.toLowerCase().includes(value));
  }

  // Display-Funktion für das Autocomplete
  displayTenant(tenant: TenantDto): string {
    return tenant ? tenant.displayName : '';
  }

  hasChild = (_: number, node: SideMenuNode) => !!node.children && node.children.length > 0;

  setTenant(tenant: TenantDto) {
    this.dataShareService.setTenant(tenant);
  }

  toggleMenu() {
    this.collapseAfterMove = false;
    const newValue = !this.menuExpanded.getValue();
    this.menuExpanded.next(newValue);
    localStorage.setItem('menu_expanded', newValue ? 'true' : 'false');
  }

  getPosition(string, subString, index) {
    return string.split(subString, index).join(subString).length;
  }

  isSelected(node: MenuModel) {
    if (this.moveMode) {
      return;
    }
    if (window.innerWidth < 1024 && localStorage.getItem('menu_expanded') === 'true') {
      this.toggleMenu();
    }
    this.router.navigate([node.link]);
  }

  /** Eingeklappt zeigt die Navigation nur Icons; das Label erscheint dann als Tooltip. */
  get isCollapsed(): boolean {
    return !this.menuExpanded.getValue();
  }

  get displayedFavorites(): SideMenuNode[] {
    return this.moveMode ? this.moveOrder : this.favorites;
  }

  /**
   * Öffnet das Favoriten-Menü an der Mausposition. Nur für favorisierbare Einträge (Blatt mit Link);
   * überall sonst bleibt das Kontextmenü des Browsers.
   * @param inFavorites Eintrag der Favoriten-Sektion – bietet ab zwei Favoriten zusätzlich „Verschieben"
   */
  openFavoriteMenu(event: MouseEvent, node: SideMenuNode, inFavorites: boolean) {
    if (this.moveMode || !this.favoritesService.isFavorable(node)) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.favoriteMenuPosition = { x: event.clientX, y: event.clientY };
    this.favoriteMenuTrigger.menuData = {
      node,
      canMove: inFavorites && this.favorites.length > 1,
      isFavorite: this.favoriteLinks.has(node.link)
    };
    this.favoriteMenuTrigger.openMenu();
  }

  addFavorite(node: SideMenuNode) {
    this.favoritesService.add(node.link);
  }

  removeFavorite(node: SideMenuNode) {
    this.favoritesService.remove(node.link);
  }

  startMove() {
    this.moveOrder = [...this.favorites];
    this.moveMode = true;
    if (!this.menuExpanded.getValue()) {
      this.collapseAfterMove = true;
      this.menuExpanded.next(true);
    }
  }

  dropFavorite(event: CdkDragDrop<SideMenuNode[]>) {
    moveItemInArray(this.moveOrder, event.previousIndex, event.currentIndex);
  }

  finishMove() {
    this.favoritesService.reorder(this.moveOrder.map((node) => node.link));
    this.endMove();
  }

  @HostListener('document:keydown.escape')
  cancelMove() {
    if (this.moveMode) {
      this.endMove();
    }
  }

  private endMove() {
    this.moveMode = false;
    this.moveOrder = [];
    if (this.collapseAfterMove) {
      this.collapseAfterMove = false;
      this.menuExpanded.next(false);
    }
  }

  mouseEnterNode(event: MouseEvent) {
    const menuItem = event.target as HTMLInputElement;

    // If it's a base Node first hide all submenu groups
    if (menuItem.getAttribute('aria-level') === '1') {
      this.hideSubmenuGroups();
    }

    // If it's got a submenu show it
    const submenuWrapper = menuItem.querySelector('.nav__group') as HTMLElement;
    if (submenuWrapper) {
      // How tall is the header
      const header = document.querySelector('.shell__header') as HTMLElement;
      const headerHeight = header.offsetHeight;

      // Where is menu item in relation to the viewport
      const menuItemOffset = menuItem.getBoundingClientRect().top;

      // How tall is the submenu
      const groupHeight = submenuWrapper.offsetHeight;

      // How much space is there to the bottom of the viewport
      const menuItemSpaceToBottom = window.innerHeight - menuItem.getBoundingClientRect().top;

      // Place the submenu in the correct position relevant to the menu item
      // Never let it get negative to avoid the submenu being cut off at the top if it's bigger than the screen
      // If there's enough space move the submenu up, so it barely fits inside the viewport
      submenuWrapper.style.top =
        Math.max(0, menuItemOffset - headerHeight - Math.max(0, groupHeight - menuItemSpaceToBottom + 7)) +
        'px';
      submenuWrapper.classList.add('nav__group--show');
    }
  }

  hideSubmenuGroups() {
    const expandables = document.querySelectorAll('.nav__group');
    if (expandables) {
      expandables.forEach((node) => {
        node.classList.remove('nav__group--show');
      });
    }
  }

  mouseEnterGroup(event: MouseEvent) {
    const group = event.target as HTMLElement;
    group.classList.add('nav__group--show');
  }

  mouseLeaveGroup(event: MouseEvent) {
    const group = event.target as HTMLElement;
    group.classList.remove('nav__group--show');
  }

  ngOnDestroy() {
    this.compUnsubs$.next();
    this.compUnsubs$.complete();
  }

  protected readonly encodeURI = encodeURI;
}
